import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useLens } from '../components/LensProvider';
import tooltips from './tooltips.json';
import walkthroughs from './walkthroughs.json';
import disclaimers from './disclaimers.json';

const NarrativeContext = createContext();

export const NarrativeProvider = ({ children }) => {
  const { pressure, phenomenology, P } = useLens();
  const [activeDisclaimer, setActiveDisclaimer] = useState(null);
  const dismissedRef = useRef(new Set());
  const prevConditionRef = useRef(null);
  const mountTimeRef = useRef(Date.now());
  const INITIAL_SUPPRESSION_MS = 5000; // Don't alarm users during first 5 seconds

  const dismissDisclaimer = useCallback(() => {
    if (activeDisclaimer) {
      // Track which disclaimer was dismissed so it doesn't re-trigger
      // while the same condition persists
      dismissedRef.current.add(prevConditionRef.current);
    }
    setActiveDisclaimer(null);
  }, [activeDisclaimer]);

  useEffect(() => {
    let conditionId = null;
    if (pressure > 0.8) {
      conditionId = 'high_pressure';
    } else if (P === 'personified') {
      conditionId = 'personified_projection';
    } else if (phenomenology === 'clarity' && pressure < 0.2) {
      conditionId = 'bliss_state';
    }

    // If condition changed from what was previously dismissed, allow re-triggering
    if (conditionId !== prevConditionRef.current) {
      // Condition changed — clear dismissed state for the NEW condition
      // (old dismissed states stay so they don't re-fire on return)
      prevConditionRef.current = conditionId;
    }

    // Only show if: condition exists, not already dismissed, not already showing, and past initial suppression
    const pastInitialDelay = (Date.now() - mountTimeRef.current) > INITIAL_SUPPRESSION_MS;
    if (conditionId && pastInitialDelay && !dismissedRef.current.has(conditionId) && !activeDisclaimer) {
      setActiveDisclaimer(disclaimers[conditionId]);
    }

    // If no condition matches, clear the dismissed set so disclaimers can
    // re-trigger on the NEXT threshold crossing
    if (!conditionId) {
      dismissedRef.current.clear();
      if (activeDisclaimer) {
        setActiveDisclaimer(null);
      }
    }
  }, [pressure, phenomenology, P, activeDisclaimer]);

  const narrativeData = {
    tooltips,
    walkthroughs,
    disclaimers,
    activeDisclaimer,
    setActiveDisclaimer: dismissDisclaimer,
  };

  return (
    <NarrativeContext.Provider value={narrativeData}>
      {children}
    </NarrativeContext.Provider>
  );
};

export const useNarrative = () => {
  return useContext(NarrativeContext);
};
